// src/modules/units/tyres/views/register/columnpicker.js
//
// The register's column picker — an INLINE panel, not a modal. It keeps the
// subsystem's move away from modals/confirm() (the yard-create and 409 panels are
// inline too), and the two-panel layout wants width, which a full-width inline
// strip gives without the focus-trap / scroll-lock / z-index machinery a dialog
// drags in.
//
// It edits a DRAFT and commits on Apply — the live table does not change until
// then, matching the screenshot's Apply / Cancel. Left: the catalogue of columns
// this mode can offer (availableColumns(ctx)), grouped, searchable, each a
// checkbox. Right: the selected columns in order, reorderable with ↑/↓ (keyboard
// and touch friendly, unlike drag-only) and removable with ×. The locked column
// (Tyre ID) is pinned first, always shown, and cannot be moved or removed.
//
// Availability, not preference, decides the catalogue: a column this mode cannot
// populate is not offered. Because the picker is only mounted where every column
// is available (the register gates it off in fitment-less modes), Apply can never
// silently strip a column that is merely hidden here.
//
// The key-list operations are pure and exported so the reorder/removal rules are
// testable without a DOM.

import {
  availableColumns, getColumn, resolveColumns, DEFAULT_COLUMN_KEYS, COLUMN_GROUPS
} from '../../shared/tyre-columns.js';
import { esc } from '../../shared/tyre-format.js';

// ---- pure key-list ops (locked-aware) -----------------------------------

/** Append a key if absent; locked-first order is preserved because locked keys
 *  are never appended (they are already present and first). */
export function keysAdd(keys, key) {
  return keys.includes(key) ? keys.slice() : [...keys, key];
}

/** Remove a key, unless it is locked. */
export function keysRemove(keys, key) {
  if (getColumn(key)?.locked) return keys.slice();
  return keys.filter((k) => k !== key);
}

/** Move a key one step (dir -1 up, +1 down). A locked key never moves, and no key
 *  may cross a locked one — so the locked Tyre ID stays first. */
export function keysMove(keys, key, dir) {
  const i = keys.indexOf(key);
  if (i < 0 || getColumn(key)?.locked) return keys.slice();
  const j = i + dir;
  if (j < 0 || j >= keys.length || getColumn(keys[j])?.locked) return keys.slice();
  const out = keys.slice();
  [out[i], out[j]] = [out[j], out[i]];
  return out;
}

// ---- component ----------------------------------------------------------

/**
 * Mount the picker into a host element (starts hidden).
 * @param {HTMLElement} hostEl
 * @param {object} opts
 * @param {object} opts.ctx                 availability context (e.g. { hasFitment })
 * @param {() => string[]} opts.getCurrentKeys  the live column keys, read on open
 * @param {(keys:string[]) => void} opts.onApply committed keys
 * @returns {{ open:()=>void, close:()=>void, toggle:()=>void, isOpen:()=>boolean, teardown:()=>void }}
 */
export function mountColumnPicker(hostEl, { ctx = {}, getCurrentKeys, onApply }) {
  let draft = [];
  let isOpen = false;

  hostEl.innerHTML = `
    <div class="tm-colpick-panel">
      <div class="tm-colpick-side tm-colpick-cat">
        <div class="tm-colpick-search">
          <span class="tm-search-icon">${''}</span>
          <input type="search" class="tm-search-input" data-cat-search placeholder="Search columns…" autocomplete="off" />
        </div>
        <div class="tm-colpick-cat-list" data-cat></div>
      </div>
      <div class="tm-colpick-side tm-colpick-sel">
        <div class="tm-colpick-sel-head">Selected columns (<span data-count>0</span>)</div>
        <div class="tm-colpick-sel-list" data-sel></div>
      </div>
    </div>
    <div class="tm-colpick-footer">
      <button type="button" class="tm-colpick-reset" data-reset>Reset to default</button>
      <div class="tm-colpick-footer-actions">
        <button type="button" class="tm-btn-secondary" data-cancel>Cancel</button>
        <button type="button" class="tm-btn-primary" data-apply>Apply</button>
      </div>
    </div>`;

  const catList = hostEl.querySelector('[data-cat]');
  const selList = hostEl.querySelector('[data-sel]');
  const countEl = hostEl.querySelector('[data-count]');
  const searchInput = hostEl.querySelector('[data-cat-search]');

  function renderCatalogue() {
    const q = (searchInput.value || '').trim().toLowerCase();
    const cols = availableColumns(ctx);
    const chosen = new Set(draft);

    const groupsHtml = COLUMN_GROUPS.map((g) => {
      const items = cols.filter((c) => c.group === g && (!q || c.header.toLowerCase().includes(q)));
      if (!items.length) return '';
      const rows = items.map((c) => {
        const checked = chosen.has(c.key) ? ' checked' : '';
        const disabled = c.locked ? ' disabled' : '';
        return `<label class="tm-colpick-opt${c.locked ? ' is-locked' : ''}">
            <input type="checkbox" data-key="${esc(c.key)}"${checked}${disabled} />
            <span>${esc(c.header)}</span>
          </label>`;
      }).join('');
      return `<div class="tm-colpick-group"><div class="tm-colpick-group-head">${esc(g)}</div>${rows}</div>`;
    }).join('');

    catList.innerHTML = groupsHtml || `<div class="tm-colpick-empty">No columns match &ldquo;${esc(q)}&rdquo;.</div>`;
  }

  function renderSelected() {
    countEl.textContent = String(draft.length);
    selList.innerHTML = draft.map((key, i) => {
      const col = getColumn(key);
      if (!col) return '';
      const locked = !!col.locked;
      const canUp = !locked && i > 0 && !getColumn(draft[i - 1])?.locked;
      const canDown = !locked && i < draft.length - 1;
      return `<div class="tm-colpick-row${locked ? ' is-locked' : ''}" data-key="${esc(key)}">
          <span class="tm-colpick-row-label">${esc(col.header)}</span>
          <span class="tm-colpick-row-actions">
            ${locked
              ? '<span class="tm-colpick-lock">Always shown</span>'
              : `<button type="button" class="tm-colpick-mini" data-up${canUp ? '' : ' disabled'} aria-label="Move ${esc(col.header)} up">&uarr;</button>
                 <button type="button" class="tm-colpick-mini" data-down${canDown ? '' : ' disabled'} aria-label="Move ${esc(col.header)} down">&darr;</button>
                 <button type="button" class="tm-colpick-x" data-remove aria-label="Remove ${esc(col.header)}">&times;</button>`}
          </span>
        </div>`;
    }).join('');
  }

  function rerender() { renderCatalogue(); renderSelected(); }

  // Search re-renders the catalogue only, so the input keeps focus mid-type.
  searchInput.addEventListener('input', renderCatalogue);

  catList.addEventListener('change', (e) => {
    const cb = e.target.closest('input[data-key]');
    if (!cb) return;
    const key = cb.getAttribute('data-key');
    draft = cb.checked ? keysAdd(draft, key) : keysRemove(draft, key);
    rerender();
  });

  selList.addEventListener('click', (e) => {
    const row = e.target.closest('[data-key]');
    if (!row) return;
    const key = row.getAttribute('data-key');
    if (e.target.closest('[data-remove]')) draft = keysRemove(draft, key);
    else if (e.target.closest('[data-up]')) draft = keysMove(draft, key, -1);
    else if (e.target.closest('[data-down]')) draft = keysMove(draft, key, +1);
    else return;
    rerender();
  });

  hostEl.querySelector('[data-reset]').addEventListener('click', () => {
    draft = [...DEFAULT_COLUMN_KEYS];
    rerender();
  });
  hostEl.querySelector('[data-cancel]').addEventListener('click', close);
  hostEl.querySelector('[data-apply]').addEventListener('click', () => {
    onApply(draft.slice());
    close();
  });

  function open() {
    // Start from the live set, normalised: valid, de-duped, locked-first.
    draft = resolveColumns(getCurrentKeys(), ctx).map((c) => c.key);
    searchInput.value = '';
    rerender();
    hostEl.hidden = false;
    isOpen = true;
    searchInput.focus();
  }
  function close() { hostEl.hidden = true; isOpen = false; }
  function toggle() { isOpen ? close() : open(); }

  hostEl.hidden = true;

  return { open, close, toggle, isOpen: () => isOpen, teardown() {} };
}
